// Shared state for "view chat result in a full page". The chat drawer
// lives inside the Header and the results page is mounted inside the
// CommodityManagementSolution routes - both sit under StandardLayout, so
// a single context provider wrapping StandardLayout is the cleanest way
// to let them share the active result without going through Redux.
import React, { createContext, useContext, useMemo, useState } from 'react';

import type { ChatQueryDescriptor } from './ChatQueryRegistry';

export interface ChatResultPayload {
    // Unique id so React can key off it if we ever render a history.
    id: string;
    // Descriptor key from the chat query registry. The full-page view
    // re-renders the same table component by looking the descriptor up
    // by this key.
    descriptorKey: ChatQueryDescriptor['key'];
    // Raw rows returned from the descriptor.execute call. We store the
    // raw data (not the React element) so the page can render at its
    // own width without being limited by the drawer's cramped column.
    rows: unknown[];
    // One-line aggregate summary already computed by the chat (e.g.
    // "12 positions, 3,400 bu, Buy 2,100 / Sell 1,300").
    summary: string;
    // Optional human-readable echo of the filters the parser picked up
    // ("Filtering by commodity Corn, fiscal year 2024.") so the user
    // sees the same context they had in the chat bubble.
    filterEcho?: string;
    timestamp: Date;
}

interface ChatResultsContextValue {
    activeResult: ChatResultPayload | undefined;
    setActiveResult: (result: ChatResultPayload | undefined) => void;
}

const ChatResultsContext = createContext<ChatResultsContextValue | undefined>(undefined);

export function ChatResultsProvider({ children }: { children: React.ReactNode }) {
    const [activeResult, setActiveResult] = useState<ChatResultPayload | undefined>(undefined);
    const value = useMemo<ChatResultsContextValue>(
        () => ({ activeResult, setActiveResult }),
        [activeResult]
    );
    return <ChatResultsContext.Provider value={value}>{children}</ChatResultsContext.Provider>;
}

export function useChatResults(): ChatResultsContextValue {
    const ctx = useContext(ChatResultsContext);
    if (!ctx) {
        throw new Error('useChatResults must be used within a ChatResultsProvider');
    }
    return ctx;
}
